import { Alert, Input, Modal, message } from 'antd';
import { useEffect, useState } from 'react';
import { duplicateCompany } from './duplicateCompanyService';

// Data-only duplicate for standing up integration-testing companies: copies
// settings, departments, user groups, subcompanies, and tiered bonus
// templates. No users, jobs, branding assets, credentials, or SSO/host.
export default function DuplicateCompanyDialog({
	sourceCompany,
	companies,
	addCompany,
	onClose,
}) {
	const [name, setName] = useState('');
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (sourceCompany) {
			setName(`${sourceCompany.name} (copy)`);
		}
	}, [sourceCompany]);

	const nameExists = (companies || []).some(
		(company) => company && company.name === name.trim()
	);

	async function duplicate() {
		if (!sourceCompany) return;
		const newName = name.trim();
		if (!newName) {
			message.error('Company name required');
			return;
		}

		setSaving(true);
		message.config({ top: 40 });
		message.loading({
			content: 'Duplicating company…',
			key: 'duplicate-company',
			duration: 0,
		});
		try {
			await duplicateCompany({
				sourceCompanyId: sourceCompany.id,
				newName,
				createCompany: addCompany,
				onProgress: (phase) =>
					message.loading({
						content: `Duplicating company: ${phase}…`,
						key: 'duplicate-company',
						duration: 0,
					}),
			});
			message.success({
				content: `"${newName}" created from "${sourceCompany.name}".`,
				key: 'duplicate-company',
				duration: 4,
			});
			onClose();
		} catch (error) {
			console.error(error);
			if (error.newCompanyId) {
				// The company row already exists (and is in the table via the
				// createCompany cache update), so closing is the honest state.
				message.error({
					content: `Duplication of "${newName}" failed while ${error.phase}. The company was created but is incomplete.`,
					key: 'duplicate-company',
					duration: 8,
				});
				onClose();
			} else if (error.phase === 'reading the source company') {
				message.error({
					content: 'Could not read the source company. Nothing was created.',
					key: 'duplicate-company',
					duration: 5,
				});
			} else {
				// Failed while (or before) creating the company itself. Almost
				// certainly nothing was created, but a dropped response could
				// hide a created row, so don't claim certainty.
				message.error({
					content:
						'Duplication failed before the new company was confirmed. Check the list before retrying.',
					key: 'duplicate-company',
					duration: 8,
				});
			}
		} finally {
			setSaving(false);
		}
	}

	return (
		<Modal
			title={sourceCompany ? `Duplicate ${sourceCompany.name}` : 'Duplicate'}
			open={!!sourceCompany}
			onCancel={onClose}
			onOk={duplicate}
			okText="Duplicate"
			confirmLoading={saving}
		>
			<div className="custom-form-group" style={{ marginBottom: 12 }}>
				<label className="custom-label">New company name</label>
				<Input
					className="custom-input"
					value={name}
					onChange={(e) => setName(e.target.value)}
				/>
			</div>
			<p style={{ marginBottom: 8 }}>
				Copies settings, departments, user groups, subcompanies, and tiered
				bonus templates. Does not copy users, jobs, branding assets,
				recognition settings, SSO/host configuration, API credentials, or
				bonus payout records.
			</p>
			{nameExists && (
				<Alert
					type="warning"
					message="A company with this name already exists. Duplicating will create a second company with the same name."
				/>
			)}
		</Modal>
	);
}
